import { Fragment, ReactElement } from 'react'
import styled from 'styled-components'

import { theme } from '@/styles/theme'
import { formatDate } from '@/utils/date'

import { officerItems } from './officerItems'
import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/layout'
import { CompanyLinks } from '@/components/layouts/Company/CompanyLinks'
import { Head } from '@/components/layouts/Head'

export const OfficerPage = () => {
  return (
    <>
      <Head
        title="役員一覧"
        description="トライアルホールディングスの経営陣を掲載しています。"
        ogp={{ type: 'article', url: 'company/officer/' }}
      />
      <OfficerMain>
        <Spacer h={{ sp: 40, pc: 69 }} />
        <SectionTitle
          titleText="Board of Directors"
          subTitleText="役員一覧"
          color="normal"
        />
        <Spacer h={{ sp: 60, pc: 136 }} />
        <OfficerList>
          {officerItems.map(({ title, name, profile }, index) => (
            <Fragment key={name}>
              {index > 0 && <Spacer h={{ sp: 30, pc: 40 }} />}
              <OfficerItem>
                <PersonalInfo>
                  <Title>{title}</Title>
                  <Name>{name}</Name>
                </PersonalInfo>
                <Profile>
                  {profile.map(({ date, content }, index, array) => (
                    <Fragment key={date}>
                      <ProfileItem>
                        <p>{date}</p>
                        <p>{content}</p>
                      </ProfileItem>
                      {index < array.length - 1 && (
                        <Spacer h={{ sp: 10, pc: 12 }} />
                      )}
                    </Fragment>
                  ))}
                </Profile>
              </OfficerItem>
              <SpacerBorder h={{ sp: 30, pc: 40 }} />
            </Fragment>
          ))}
        </OfficerList>
        <Spacer h={{ sp: 30, pc: 30 }} />
        <EnactmentDate>
          {formatDate('2026-09-25', 'yyyy年M月d日')}
        </EnactmentDate>
        <CompanyLinks padding={{ pc: '130px 0 0' }} />
        <Spacer h={{ sp: 70, pc: 130 }} />
      </OfficerMain>
    </>
  )
}
OfficerPage.getLayout = function getLayout(page: ReactElement) {
  const pageTitle = {
    title: 'Corporate Information',
    subTitle: '企業情報',
  }

  const breadCrumbs = [
    { name: 'ホーム', path: '/' },
    { name: '企業情報', path: '/company/' },
    { name: '役員一覧', path: '' },
  ]
  return (
    <TitleLayout {...pageTitle} breadCrumbs={breadCrumbs} isRequiredBackground>
      {page}
    </TitleLayout>
  )
}

const OfficerMain = styled.div`
  width: 1040px;
  margin: 0 auto;

  @media screen and (max-width: 1047px) {
    width: 100%;
    padding: 0 20px;
  }
`

const OfficerList = styled.ul`
  width: 100%;
`

const OfficerItem = styled.li`
  display: flex;
  gap: 37px;

  @media screen and (max-width: 1047px) {
    flex-direction: column;
    gap: 28px;
  }
`

const SpacerBorder = styled(Spacer)`
  :not(:last-of-type) {
    border-bottom: 1px solid ${theme.colors.grey};
  }
`

const PersonalInfo = styled.div`
  width: 210px;

  @media screen and (max-width: 1047px) {
    width: 100%;
  }
`

const Title = styled.p`
  font-size: 18px;
  line-height: 26px;
  letter-spacing: 1.8px;

  @media screen and (max-width: 1047px) {
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 1.6px;
  }
`

const Name = styled.p`
  font-size: 24px;
  line-height: 35px;
  letter-spacing: 2.4px;
  font-weight: bold;
  color: ${theme.colors.trialBlue};

  @media screen and (max-width: 1047px) {
    font-size: 20px;
    line-height: 29px;
    letter-spacing: 2px;
  }
`

const Profile = styled.ul`
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 1.6px;
  flex: 1;

  @media screen and (max-width: 1047px) {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 1.4px;
  }
`

const ProfileItem = styled.li`
  display: flex;
  gap: 20px;

  p:first-child {
    width: 114px;
  }

  p:last-child {
    flex: 1;
  }

  @media screen and (max-width: 1047px) {
    gap: 10px;

    p::first-child {
      width: 90px;
    }
  }
`
const EnactmentDate = styled.p`
  text-align: right;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 1.6px;
  @media screen and (max-width: 1047px) {
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 1.4px;
  }
`
